// A synthesized announcer in the manner of a game character's chatter: one
// short pitched syllable per letter, quick enough that the letters blur into
// a voice. Nothing is recorded; each syllable is a buzz through two formant
// filters, and the letter picks the vowel and the note. In Japanese a kana
// is a syllable with its own vowel (か is "a", き "i"), a kanji takes a vowel
// and a note from its code point, and 。、！？ end a phrase the way . ! ? do.

/** First and second formant per vowel sound, in Hz. */
const FORMANTS: Record<string, [number, number]> = {
  a: [800, 1250], e: [520, 1900], i: [320, 2300], o: [500, 900], u: [350, 800],
};
const VOWELS = "aeiou";
/** Semitones each letter sits from the speaker's pitch, so words have a tune. */
const NOTE = [0, 3, -2, 5, 2, -4, 1, 4, 7, -1, 3, 0, -3, 2, -2, 5, 1, -5, 4, 0, -4, 2, 6, -1, 3, -2];
/** Hiragana by row, each followed by its vowel: the vowel of a kana is its column. */
const KANA_ROWS = "あいうえお かきくけこ がぎぐげご さしすせそ ざじずぜぞ たちつてと だぢづでど なにぬねの はひふへほ ばびぶべぼ ぱぴぷぺぽ まみむめも や_ゆ_よ らりるれろ わ___を ぁぃぅぇぉ ゃ_ゅ_ょ";
const KANA_VOWEL = new Map<string, string>();
/** The vowels of the kana's columns, in the table's order. */
const KANA_COLUMNS = "aiueo";
for (const row of KANA_ROWS.split(" ")) [...row].forEach((kana, i) => kana !== "_" && KANA_VOWEL.set(kana, KANA_COLUMNS[i]!));
KANA_VOWEL.set("ん", "u").set("っ", "u");

/**
 * The sound one character makes: a note index into NOTE and its vowel, or
 * null for a character that makes none. `end` marks the end of a phrase.
 */
export function soundOf(char: string): { note: number; vowel: string | null; end: boolean } | null {
  if (char === "." || char === "!" || char === "?" || char === "。" || char === "！" || char === "？") return { note: 0, vowel: null, end: true };
  const lower = char.toLowerCase();
  const code = lower.charCodeAt(0) - 97;
  if (code >= 0 && code <= 25) return { note: code, vowel: VOWELS.includes(lower) ? lower : null, end: false };
  const cp = char.codePointAt(0)!;
  // Katakana sits 0x60 above the hiragana with the same sound.
  const kana = cp >= 0x30a1 && cp <= 0x30f6 ? String.fromCodePoint(cp - 0x60) : char;
  const vowel = KANA_VOWEL.get(kana);
  if (vowel) return { note: kana.codePointAt(0)! % 26, vowel, end: false };
  if (cp >= 0x3400 && cp <= 0x9fff) return { note: cp % 26, vowel: VOWELS[cp % 5]!, end: false };
  return null;
}

export class Babble {
  private context: AudioContext | null = null;
  private current: GainNode | null = null;
  private volume = 0.5;
  muted = false;

  /**
   * Browsers start audio only from a user's gesture. Call this inside one
   * (the click that opens the television); later calls are free.
   */
  unlock(): void {
    try {
      this.context ??= new AudioContext();
      if (this.context.state === "suspended") void this.context.resume();
    } catch {
      this.context = null;
    }
  }

  /** Read `text` at `rate` letters per second. A new line replaces the one being read. */
  say(text: string, rate: number): void {
    this.stop();
    const context = this.context;
    if (!context || this.muted || context.state !== "running") return;
    const out = context.createGain();
    out.gain.value = this.volume * 0.6;
    out.connect(context.destination);
    this.current = out;
    this.schedule(context, out, text, rate, context.currentTime + 0.04);
  }

  /** Lay the syllables of `text` on `context`'s clock from `start`, into `out`. */
  private schedule(context: BaseAudioContext, out: AudioNode, text: string, rate: number, start: number): void {
    const step = 1 / rate;
    // The speaker's pitch drifts down across a sentence and resets after it.
    let fall = 0;
    let vowel = "a";
    for (let i = 0; i < text.length; i++) {
      const sound = soundOf(text[i]!);
      if (!sound) continue;
      if (sound.end) {
        fall = 0;
        continue;
      }
      fall += 0.06;
      if (sound.vowel) vowel = sound.vowel;
      const near = (mark: string) => text.indexOf(mark, i) >= 0 && text.indexOf(mark, i) - i < 6;
      const asks = near("?") || near("？");
      const semitone = NOTE[sound.note]! * 0.5 - Math.min(4, fall) + (asks ? 4 : 0);
      this.syllable(context, out, start + i * step, step, 250 * 2 ** (semitone / 12), FORMANTS[vowel]!, sound.vowel !== null);
    }
  }

  private syllable(context: BaseAudioContext, out: AudioNode, at: number, length: number, pitch: number, formant: [number, number], open: boolean): void {
    const buzz = context.createOscillator();
    buzz.type = "sawtooth";
    buzz.frequency.setValueAtTime(pitch, at);
    // Each syllable scoops up into its note, the way a spoken one does.
    buzz.frequency.linearRampToValueAtTime(pitch * 1.06, at + length * 0.5);
    const envelope = context.createGain();
    const peak = open ? 1 : 0.6;
    envelope.gain.setValueAtTime(0, at);
    envelope.gain.linearRampToValueAtTime(peak, at + 0.008);
    envelope.gain.exponentialRampToValueAtTime(0.001, at + length * (open ? 1.5 : 1.1));
    for (const [index, frequency] of formant.entries()) {
      const band = context.createBiquadFilter();
      band.type = "bandpass";
      band.frequency.value = frequency;
      band.Q.value = index === 0 ? 5 : 8;
      buzz.connect(band);
      band.connect(envelope);
    }
    envelope.connect(out);
    buzz.start(at);
    buzz.stop(at + length * 1.6);
  }

  /** Cut the line being read. */
  stop(): void {
    if (!this.current || !this.context) return;
    const out = this.current;
    this.current = null;
    out.gain.setTargetAtTime(0, this.context.currentTime, 0.015);
    setTimeout(() => out.disconnect(), 120);
  }

  dispose(): void {
    this.stop();
    void this.context?.close();
    this.context = null;
  }
}
